import { useEffect, useRef } from "react";
import { useTheme } from "@/lib/theme-context";

/**
 * Cloudflare Turnstile widget (managed / invisible mode).
 *
 * Loads the Turnstile script once, renders a widget for the given site key, and
 * calls `onVerify` with the token when the visitor passes. The widget is
 * theme-aware (light / dark) to match the site. It runs in "interaction-only"
 * appearance, so genuine visitors usually pass with a single tap — or nothing
 * at all — and only see an interactive challenge if Cloudflare deems it needed.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          appearance?: "always" | "execute" | "interaction-only";
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
        },
      ) => string;
      remove: (id: string) => void;
      reset: (id?: string) => void;
    };
  }
}

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject());
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export default function TurnstileWidget({
  siteKey,
  onVerify,
  onError,
}: {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: () => void;
}) {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        // Clear any previous render (e.g. after a theme change).
        containerRef.current.innerHTML = "";
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: theme === "light" ? "light" : "dark",
          appearance: "interaction-only",
          callback: (token: string) => onVerify(token),
          "error-callback": () => onError?.(),
          "expired-callback": () => onError?.(),
        });
      })
      .catch(() => onError?.());

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* already removed */
        }
        widgetIdRef.current = null;
      }
    };
    // Re-render when the site key or theme changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, theme]);

  return <div ref={containerRef} className="flex justify-center" />;
}
